import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Download, X, Loader2, Check } from 'lucide-react';
import { generateShareImage, shareImageFile, downloadImage } from '../../utils/share';
import { RankingRoundStoryCard, type RoundMatchLine } from './RankingRoundStoryCard';
import { trackEvent } from '../../utils/analytics';
import type { RankingMatchDetail } from '../../types';

interface RankingRoundShareButtonProps {
  rankingId: string;
  rankingName: string;
  imageUrl: string | null;
  roundNumber: number;
  matches: RankingMatchDetail[];
}

type ShareState = 'idle' | 'preparing' | 'ready' | 'sharing' | 'success' | 'error';

const CORS_BLOCKLIST = ['ui-avatars.com'];

async function urlToDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { cache: 'no-store' });
    const blob = await response.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function isCorsBlocked(url: string): boolean {
  return CORS_BLOCKLIST.some(host => url.includes(host));
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
}

function toMatchLines(matches: RankingMatchDetail[]): RoundMatchLine[] {
  return matches.map(m => ({
    team1: `${m.team1[0].name?.split(' ')[0] || '?'} + ${m.team1[1].name?.split(' ')[0] || '?'}`,
    team2: `${m.team2[0].name?.split(' ')[0] || '?'} + ${m.team2[1].name?.split(' ')[0] || '?'}`,
    score: `${m.team1Games} x ${m.team2Games}${m.tiebreakScore != null ? ` (${m.tiebreakScore})` : ''}`,
    isWo: m.isWo,
  }));
}

export function RankingRoundShareButton({
  rankingId: _rankingId,
  rankingName,
  imageUrl,
  roundNumber,
  matches,
}: RankingRoundShareButtonProps) {
  const [state, setState] = useState<ShareState>('idle');
  const [showModal, setShowModal] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturePhotoUrl, setCapturePhotoUrl] = useState<string | null>(null);
  const [captureRankingImageUrl, setCaptureRankingImageUrl] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const matchLines = toMatchLines(matches);
  const filename = `ranking-rodada-${roundNumber}`;

  const generateImage = useCallback(async (photoDataUrl: string) => {
    const inlinedRanking = imageUrl && !isCorsBlocked(imageUrl) ? await urlToDataUrl(imageUrl) : null;
    setCaptureRankingImageUrl(inlinedRanking);
    setCapturePhotoUrl(photoDataUrl);
    // Wait for re-render with inlined data URLs
    await new Promise(resolve => setTimeout(resolve, 150));
    if (!cardRef.current) return;
    try {
      const blob = await generateShareImage(cardRef.current);
      if (blob) {
        setImageBlob(blob);
        setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
        setState('ready');
      } else {
        setState('error');
      }
    } catch (error) {
      console.error('Error generating ranking round image:', error);
      trackEvent('btv_ranking_round_share_error', { error: String(error) });
      setState('error');
    }
  }, [imageUrl]);

  const handleTrigger = () => {
    trackEvent('btv_ranking_round_share_attempt');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    trackEvent('btv_ranking_round_share_photo_selected');
    setShowModal(true);
    setState('preparing');

    try {
      const photoDataUrl = await fileToDataUrl(file);
      await generateImage(photoDataUrl);
    } catch (error) {
      console.error('Error reading photo:', error);
      trackEvent('btv_ranking_round_share_error', { error: String(error) });
      setState('error');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setState('idle');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setImageBlob(null);
    setCapturePhotoUrl(null);
  };

  const handleShare = async () => {
    if (!imageBlob) return;
    setState('sharing');

    const shared = await shareImageFile(
      imageBlob,
      `Ranking ${rankingName}`,
      `Confira os resultados da Rodada ${roundNumber}! 🏆`,
    );

    if (shared) {
      trackEvent('btv_ranking_round_share_success', { method: 'web_share_api' });
    } else {
      downloadImage(imageBlob, filename);
      trackEvent('btv_ranking_round_share_download', { reason: 'web_share_fallback' });
    }
    setState('success');
    setTimeout(handleClose, 1500);
  };

  const handleDownload = () => {
    if (!imageBlob) return;
    downloadImage(imageBlob, filename);
    trackEvent('btv_ranking_round_share_download', { reason: 'direct_download' });
    setState('success');
    setTimeout(handleClose, 1500);
  };

  const handleRetry = () => {
    handleClose();
    setTimeout(() => fileInputRef.current?.click(), 100);
  };

  return (
    <>
      <button
        onClick={handleTrigger}
        title="Compartilhar rodada"
        aria-label="Compartilhar rodada"
        className="flex items-center justify-center w-8 h-8 bg-white/10 hover:bg-white/20 rounded-lg text-white/70 hover:text-white transition-colors"
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {showModal &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/95 backdrop-blur-sm"
              onClick={handleClose}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-sm"
              >
                <button
                  onClick={handleClose}
                  className="absolute -top-12 right-0 p-2 text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="flex flex-col items-center">
                  {state === 'preparing' && (
                    <div className="w-[280px] h-[500px] rounded-3xl flex items-center justify-center bg-ocean-blue">
                      <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
                    </div>
                  )}

                  {state === 'ready' && previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-[280px] rounded-3xl shadow-2xl"
                    />
                  )}

                  {state === 'sharing' && (
                    <div className="w-[280px] h-[500px] rounded-3xl flex items-center justify-center bg-ocean-blue">
                      <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
                    </div>
                  )}

                  {state === 'success' && (
                    <div className="w-[280px] h-[500px] rounded-3xl flex flex-col items-center justify-center gap-4 bg-ocean-blue">
                      <div className="w-16 h-16 rounded-full bg-success-green/20 flex items-center justify-center">
                        <Check className="w-8 h-8 text-success-green" />
                      </div>
                      <p className="text-white font-medium">Pronto!</p>
                    </div>
                  )}

                  {state === 'error' && (
                    <div className="w-[280px] h-[500px] rounded-3xl flex flex-col items-center justify-center gap-4 p-6 bg-ocean-blue">
                      <p className="text-coral font-medium">Erro ao gerar imagem</p>
                      <button
                        onClick={handleRetry}
                        className="px-4 py-2 bg-white/10 rounded-lg text-white/80"
                      >
                        Tentar novamente
                      </button>
                    </div>
                  )}

                  {state === 'ready' && (
                    <div className="flex gap-3 mt-4">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleDownload}
                        className="flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Baixar</span>
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleShare}
                        className="flex items-center gap-1.5 px-4 py-2 bg-turquoise hover:bg-turquoise/90 rounded-lg text-deep-blue font-medium text-sm transition-colors"
                      >
                        <Share2 className="w-4 h-4" />
                        <span>Compartilhar</span>
                      </motion.button>
                    </div>
                  )}
                </div>

                {/* Hidden card for capture — no external URLs to avoid CORS */}
                <div className="fixed -left-[9999px] top-0">
                  <RankingRoundStoryCard
                    ref={cardRef}
                    rankingName={rankingName}
                    roundNumber={roundNumber}
                    rankingImageUrl={captureRankingImageUrl}
                    photoDataUrl={capturePhotoUrl}
                    matches={matchLines}
                  />
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
